import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap } from 'rxjs/operators';

import { BroadcastService, GridService, NotificationService, messageType } from '@core/services';

import { AuthService, AuthUtilService } from '@auth/services';
import { SystemService } from '@system/services';

import * as AuthActions from './auth.actions';
import * as fromAuth from './auth.selectors';

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private router: Router,
    private authService: AuthService,
    private store: Store,
    private broadcastService: BroadcastService,
    private notificationService: NotificationService,
    private authUtilService: AuthUtilService,
    private gridService: GridService,
    private systemService: SystemService
  ) {}

  Login$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.Login),
      switchMap(({ username, password }) => {
        this.authService.updateLastLogin(0);

        return this.authService.login(username, password).pipe(
          map((payload) => AuthActions.LoginSuccess({ payload })),
          catchError((error: HttpErrorResponse) => of(AuthActions.LoginFailure({ error })))
        );
      })
    );
  });

  LoginSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.LoginSuccess),
      map(() => {
        this.authService.loginEvent$.next();
        return AuthActions.Permissions();
      })
    );
  });

  LoginFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.LoginFailure),
        map(({ error }) => {
          this.authService.loginError$.next(error);
        })
      );
    },
    { dispatch: false }
  );

  LoginFromBroadcast$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.LoginFromBroadcast),
      map(({ redirect }) => AuthActions.LoginFromBroadcastSuccess({ redirect }))
    );
  });

  LoginFromBroadcastSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.LoginFromBroadcastSuccess),
        concatLatestFrom(() => [this.store.select(fromAuth.selectScreens), this.authService.changePending$]),
        map(([{ redirect }, screens, pending]) => {
          this.authService.updateLastLogin();

          if (redirect) {
            setTimeout(() => {
              void this.router.navigateByUrl(redirect);
            }, 250);
          } else {
            if (screens?.currentScreen?.route && !pending) {
              void this.router.navigate([screens.currentScreen.route]);
            } else {
              void this.router.navigate(['/home']);
            }
          }
        })
      );
    },
    { dispatch: false }
  );

  Permissions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.Permissions),
      switchMap(() =>
        this.authService.getPermissions().pipe(
          map((payload) => AuthActions.PermissionsSuccess({ payload })),
          catchError((error: HttpErrorResponse) => of(AuthActions.PermissionsFailure({ error })))
        )
      )
    );
  });

  PermissionsSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.PermissionsSuccess),
        concatLatestFrom(() => [this.authService.screens$, this.authService.changePending$, this.authService.state$]),
        map(([_, screens, pending, authState]) => {
          this.authService.updateLastLogin();
          this.systemService.requestLists();

          // announce that this client has logged in
          this.broadcastService.send({
            type: messageType.login,
            payload: {
              authState: {
                ...authState,
                id: null,
              },
              redirect: null,
            },
          });

          if (screens?.currentScreen?.route && !pending) {
            void this.router.navigate([screens.currentScreen.route]);
          } else {
            void this.router.navigate(['/home']);
          }
        })
      );
    },
    { dispatch: false }
  );

  Logout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.Logout),
      mergeMap(() => {
        this.authService.updateLastLogin(0);
        this.broadcastService.send({ type: messageType.logout });

        return this.authService.logout().pipe(
          map(() => AuthActions.LogoutSuccess()),
          catchError((error: HttpErrorResponse) => of(AuthActions.LogoutFailure(error)))
        );
      })
    );
  });

  LogoutSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.LogoutSuccess, AuthActions.LogoutFailure),
        map(() => void this.router.navigate(['/login']))
      );
    },
    { dispatch: false }
  );

  ChangeCustomer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.ChangeCustomer),
      switchMap(({ custid }) =>
        this.authService.changeCustomer(custid).pipe(
          map((payload) => AuthActions.ChangeCustomerSuccess({ payload })),
          catchError((error: HttpErrorResponse) => of(AuthActions.ChangeCustomerFailure(error)))
        )
      )
    );
  });

  ChangeCustomerSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.ChangeCustomerSuccess),
        concatLatestFrom(() => this.store.select(fromAuth.selectScreens)),
        map(([{ payload }, screens]) => {
          this.gridService.refreshAllGrids();

          this.broadcastService.send({ type: messageType.changeCustomer, payload });

          if (screens?.currentScreen?.route) {
            void this.router.navigate([screens.currentScreen.route]);
          } else {
            void this.router.navigate(['/home']);
          }
        })
      );
    },
    { dispatch: false }
  );

  ChangeCustomerFromBroadcast$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.ChangeCustomerFromBroadcast),
        concatLatestFrom(() => this.store.select(fromAuth.selectScreens)),
        map(([_, screens]) => {
          this.gridService.refreshAllGrids();

          if (screens?.currentScreen?.route) {
            void this.router.navigate([screens.currentScreen.route]);
          } else {
            void this.router.navigate(['/home']);
          }
        })
      );
    },
    { dispatch: false }
  );

  ChangeCustomerFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.ChangeCustomerFailure),
        map(({ error }) => {
          const { title, message } = this.authUtilService.getErrorMessage(error);

          this.notificationService.error({
            title,
            message,
          });
        })
      );
    },
    { dispatch: false }
  );

  ChangeFacility$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.ChangeFacility),
      switchMap(({ facility }) =>
        this.authService.changeFacility(facility).pipe(
          map((payload) => AuthActions.ChangeFacilitySuccess({ payload })),
          catchError((error: HttpErrorResponse) => of(AuthActions.ChangeFacilityFailure(error)))
        )
      )
    );
  });

  ChangeFacilitySuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.ChangeFacilitySuccess),
        concatLatestFrom(() => this.store.select(fromAuth.selectScreens)),
        map(([{ payload }, screens]) => {
          this.gridService.refreshAllGrids();

          this.broadcastService.send({ type: messageType.changeFacility, payload });

          if (screens?.currentScreen?.route) {
            void this.router.navigate([screens.currentScreen.route]);
          } else {
            void this.router.navigate(['/home']);
          }
        })
      );
    },
    { dispatch: false }
  );

  ChangeFacilityFromBroadcast$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.ChangeFacilityFromBroadcast),
        concatLatestFrom(() => this.store.select(fromAuth.selectScreens)),
        map(([_, screens]) => {
          this.gridService.refreshAllGrids();

          if (screens?.currentScreen?.route) {
            void this.router.navigate([screens.currentScreen.route]);
          } else {
            void this.router.navigate(['/home']);
          }
        })
      );
    },
    { dispatch: false }
  );

  ChangeFacilityFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.ChangeFacilityFailure),
        map(({ error }) => {
          const { title, message } = this.authUtilService.getErrorMessage(error);

          this.notificationService.error({
            title,
            message,
          });
        })
      );
    },
    { dispatch: false }
  );

  ChangeLanguage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.ChangeLanguage),
      concatLatestFrom(() => this.store.select(fromAuth.selectAuthenticated)),
      filter(([_, authenticated]) => !!authenticated),
      switchMap(([{ language }, _]) =>
        this.authService.changeLanguage(language).pipe(
          map((payload) => AuthActions.ChangeLanguageSuccess({ payload })),
          catchError((error: HttpErrorResponse) => of(AuthActions.ChangeLanguageFailure(error)))
        )
      )
    );
  });

  ChangeLanguageSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.ChangeLanguage),
        map(({ language }) => {
          this.broadcastService.send({ type: messageType.language, payload: language });
        })
      );
    },
    { dispatch: false }
  );

  ChangeTheme$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.ChangeTheme),
      concatLatestFrom(() => this.store.select(fromAuth.selectAuthenticated)),
      filter(([_, authenticated]) => !!authenticated),
      switchMap(([{ theme }, _]) =>
        this.authService.changeTheme(theme).pipe(
          map(() => AuthActions.ChangeThemeSuccess({ theme })),
          catchError((error: HttpErrorResponse) => of(AuthActions.ChangeThemeFailure(error)))
        )
      )
    );
  });

  ChangeThemeFromBroadcast$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.ChangeThemeFromBroadcast),
        map(({ theme }) => this.authService.setTheme(theme))
      );
    },
    { dispatch: false }
  );

  SetTheme$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.ChangeTheme),
        map(({ theme }) => {
          this.broadcastService.send({ type: messageType.theme, payload: theme });
          this.authService.setTheme(theme);
        })
      );
    },
    { dispatch: false }
  );

  SetThemeAfterAuth$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.LoginSuccess),
        map(({ payload }) => {
          this.broadcastService.send({ type: messageType.theme, payload: payload.theme });
          this.authService.setTheme(payload.theme);
        })
      );
    },
    { dispatch: false }
  );

  SessionPing$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.SessionPing),
      switchMap(() =>
        this.authService.ping().pipe(
          map((data) => AuthActions.SessionPingSuccess({ data })),
          catchError((error: HttpErrorResponse) => of(AuthActions.SessionPingFailure(error)))
        )
      )
    );
  });

  SessionPingFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.SessionPingFailure),
        map(() => {
          this.authService.timeoutDialog();
          this.broadcastService.send({ type: messageType.timeout, payload: true });
        })
      );
    },
    { dispatch: false }
  );

  LoginFromTimeout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.LoginFromTimeout),
      switchMap(({ username, password }) => {
        this.authService.updateLastLogin(0);

        return this.authService.login(username, password).pipe(
          map((payload) => AuthActions.LoginFromTimeoutSuccess({ payload })),
          catchError((error: HttpErrorResponse) => of(AuthActions.LoginFromTimeoutFailure({ error })))
        );
      })
    );
  });

  LoginFromTimeoutSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.LoginFromTimeoutSuccess),
        map(() => {
          this.systemService.requestLists();
          this.broadcastService.send({ type: messageType.timeout, payload: false });
          this.authService.loginEvent$.next();
        })
      );
    },
    { dispatch: false }
  );

  LoginFromTimeoutFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.LoginFromTimeoutFailure),
        map(({ error }) => {
          this.authService.loginError$.next(error);
        })
      );
    },
    { dispatch: false }
  );
}
